import React from "react";
import { Form } from "antd";
import { useSelector } from "react-redux";
import { formSectionsController, vendors } from "../../formSectionsController";
import { ConfigurableRow, ROW_OPTIONS } from "../../ConfigurableRow";
import { useGetSetting } from "../../../../../hooks/getSetting";
import { selectCurrentApplication } from "../../../../../store/selector";
import { isPatientUnder18 } from "../../utils/caregiverUtils";
import { calculateAge } from "../../utils/parsers";

/**
 * Unified Caregiver Section Component
 *
 * Handles both PAP (user-choice) and Under-18 (automatic) caregiver scenarios
 * Reuses existing PAP form components for consistency
 */
export const UnifiedCaregiverSection = ({
  alternativeContactField = null,
  patientDobField = null,
  drugNameField = null,
  featureFlagKey = "fe.caregiver-under-18-enrollment",
  formConfiguration = null,
  vendor = vendors.GENENTECH_PAP,
  fieldsRequired = true,
  customFields = {},
  includeAddress = false // Include address fields (only for copay)
}) => {
  const form = Form.useFormInstance();
  const [isCaregiverFeatureEnabled] = useGetSetting([featureFlagKey]);
  const currentApplication = useSelector(selectCurrentApplication);

  const watchedAlternativeContact = Form.useWatch(alternativeContactField, form);
  const watchedPatientDob = Form.useWatch(patientDobField, form);
  const watchedDrugName = Form.useWatch(drugNameField, form);

  const alternativeContact = alternativeContactField ? watchedAlternativeContact : null;
  const patientDob = patientDobField ? watchedPatientDob : null;
  const drugName = drugNameField ? watchedDrugName : null;

  const applicationDrugName =
    currentApplication?.drugDisplayName ||
    currentApplication?.associatedDrugs?.[0] ||
    currentApplication?.potentialSaving?.drugDisplayName;

  const patientFormComponents = formSectionsController().getSectionFieldsByVendor(vendor)().patient;

  const showCaregiverSection = (() => {
    if (alternativeContactField && alternativeContact) return true;

    if (patientDobField && isCaregiverFeatureEnabled && patientDob) {
      const isUnder18 = isPatientUnder18(patientDob);
      const patientAge = calculateAge(patientDob);

      if (!isUnder18) return false;

      if (formConfiguration?.pediatricDrugs?.getCaregiverEligibility) {
        const finalDrugName = applicationDrugName || (drugNameField && typeof drugName === "string" ? drugName : null);
        const result = formConfiguration.pediatricDrugs.getCaregiverEligibility(
          patientAge,
          finalDrugName,
          formConfiguration.pediatricDrugs.eligibleDrugs
        );
        return result;
      }

      return isUnder18;
    }

    return false;
  })();

  if (!showCaregiverSection) return null;

  return (
    <>
      {patientFormComponents.patient_divider()}
      <ConfigurableRow>
        {[
          patientFormComponents.relationship_to_patient({
            required: fieldsRequired,
            ...customFields.relationship
          })
        ]}
      </ConfigurableRow>
      <ConfigurableRow config={ROW_OPTIONS.EQUAL}>
        {[
          patientFormComponents.related_firstName({
            required: fieldsRequired,
            ...customFields.firstName
          }),
          patientFormComponents.related_lastName({
            required: fieldsRequired,
            ...customFields.lastName
          }),
          patientFormComponents.related_phone_number({
            required: fieldsRequired,
            ...customFields.phone
          }),
          patientFormComponents.related_additional_phone_number({
            required: false,
            ...customFields.additionalPhone
          })
        ]}
      </ConfigurableRow>

      {includeAddress && (
        <>
          <ConfigurableRow config={ROW_OPTIONS.EQUAL}>
            {[
              patientFormComponents.related_dob({
                required: fieldsRequired,
                ...customFields.dob
              }),
              patientFormComponents.related_address({
                required: fieldsRequired,
                ...customFields.address
              }),
              patientFormComponents.related_city({
                required: fieldsRequired,
                ...customFields.city
              }),
              patientFormComponents.related_state({
                required: fieldsRequired,
                ...customFields.state
              })
            ]}
          </ConfigurableRow>
          <ConfigurableRow config={ROW_OPTIONS.PROPORTION_1_BLANK}>
            {[
              patientFormComponents.related_zip({
                required: fieldsRequired,
                ...customFields.zip
              })
            ]}
          </ConfigurableRow>
        </>
      )}

      {!includeAddress && (
        <ConfigurableRow config={ROW_OPTIONS.PROPORTION_1_BLANK}>
          {[
            patientFormComponents.related_dob({
              required: fieldsRequired,
              ...customFields.dob
            })
          ]}
        </ConfigurableRow>
      )}

      <ConfigurableRow config={ROW_OPTIONS.PROPORTION_1_BLANK}>
        {[
          patientFormComponents.signed_patient_authorization({
            required: false,
            ...customFields.authorization
          })
        ]}
      </ConfigurableRow>
    </>
  );
};

export default UnifiedCaregiverSection;
